import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  IconChannelAnalytics,
  IconChannelCall,
  IconChannelPatients,
  IconChannelSMS,
  IconChannelWhatsApp,
  IconEmail,
  IconAuthError,
  IconEye,
  IconEyeOff,
  IconPassword,
  IconAuthSpinner,
  IconAuthSuccess,
  IconAuthUser,
} from '../components/icons';
import { useAuth } from '../context/AuthContext';
import { cn } from '../utils/cn';

/**
 * Auth fields are pill-shaped with a leading glyph, which is deliberately
 * softer than the rounded-lg used through the signed-in product: the front
 * door is allowed to feel different from the workbench behind it.
 *
 * pl-11 reserves the glyph's column so the caret never lands under it.
 */
const inputCls = (hasError) =>
  cn(
    'h-12 w-full rounded-full border bg-white pl-11 pr-4 text-sm text-gray-900 placeholder-gray-400',
    'outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20',
    'disabled:opacity-50',
    hasError ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'
  );

/**
 * A field with its icon parked in the reserved left column.
 *
 * 18px at `bold`, not the 16px `regular` used through the rest of the product.
 * Rendered side by side across every weight and size, `regular` at 16px is
 * visibly wiry inside a 48px pill — the envelope reads as a scratched outline
 * next to 14px text. `bold` at 18px carries the same optical weight as the
 * label beside it, which is what makes a glyph look drawn rather than traced.
 * `fill` was the other candidate and loses the envelope's internal detail.
 */
const Field = ({ icon: Icon, children }) => (
  <div className="relative">
    <Icon
      size={18}
      aria-hidden="true"
      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
    />
    {children}
  </div>
);

/**
 * Artwork for the left panel.
 *
 * Trimmed to its opaque bounds by scripts/trim_artwork.py, and that trim is
 * what makes centring work at all. Canva exports a cut-out onto a fixed canvas
 * with asymmetric transparent padding — this one had 58px of it on the left
 * and 77px on the right — so centring the file left the mother visibly off the
 * orbit's centre. Re-run the script after any re-export.
 */
const HERO_IMAGE = '/preeclampsia-early-warning.png';

/**
 * Orbit geometry.
 *
 * Everything below is measured against the stage, not against the panel, which
 * is what makes the halo, the ring, the chips and the artwork concentric by
 * construction: they are all children of one square box and all centred on it.
 * The previous version centred the ring on the panel and the artwork on a
 * separate padded column, which left them 12px apart.
 *
 * STAGE caps that box at the width of the panel's content column, so the
 * ring's outer edge, the logo and the footer all land on the same 32px margin.
 */
const STAGE = 384;
const CHIP = 44;

/**
 * Chip centres ride the stage's inscribed circle: `50% - CHIP/2`, resolved by
 * the browser rather than by us, so the ring keeps its proportions when the
 * stage shrinks. It does shrink: between the lg breakpoint and the width at
 * which the card reaches max-w-5xl, the content column is 352px, not 384px.
 * A hard-coded radius overhangs that column by 16px a side — not enough to be
 * clipped, since the panel's padding absorbs it, but enough to break the 32px
 * margin the logo and the footer are aligned to.
 */
const ORBIT_R = `(50% - ${CHIP / 2}px)`;

/**
 * The light pool the artwork sits on.
 *
 * A radial fade rather than a flat disc: the artwork is an organic blob, and a
 * circle with a crisp edge behind it reads as a mistake wherever the two
 * outlines disagree. The gradient holds steady behind the blob and reaches
 * fully transparent at the edge of its own box, which is sized to stop short
 * of the chips' inner sweep, so it never collides with them either.
 */
const HALO = '83.33%';
const HALO_FILL =
  'radial-gradient(circle, rgba(255,255,255,0.13) 0%, rgba(255,255,255,0.13) 78%, rgba(255,255,255,0) 100%)';

/**
 * Artwork height, as a share of the stage. The chips sweep an inner edge at
 * 50% - CHIP, and the trimmed artwork is near enough square, so 68% leaves an
 * even channel between artwork and chips the whole way round: ~18px at the
 * full 384px stage, ~12px at the narrowest 352px one.
 */
const HERO_H = '68%';

/**
 * The channels a message can actually travel down, orbiting the artwork.
 *
 * Evenly divided at 72deg. An earlier version hand-placed these angles to keep
 * a chip off the mother's face, which the rotation defeats anyway: every chip
 * visits every angle over one period. Clearance is a radius problem, not an
 * angle problem, and HERO_H is where it is actually solved.
 */
const CHANNELS = [
  { icon: IconChannelWhatsApp, label: 'WhatsApp' },
  { icon: IconChannelCall, label: 'Voice calls' },
  { icon: IconChannelSMS, label: 'SMS' },
  { icon: IconChannelAnalytics, label: 'Analytics' },
  { icon: IconChannelPatients, label: 'Patient records' },
];

const SignIn = () => {
  const { login, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 'login' | 'signup'
  const [mode, setMode] = useState('login');

  // Shared fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // Drives which web storage holds the token: localStorage persists across
  // browser restarts, sessionStorage does not. See utils/tokenStore.
  const [remember, setRemember] = useState(true);

  // Sign-up only
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const from = location.state?.from?.pathname || '/';

  const switchMode = (m) => {
    setMode(m);
    setError('');
    setSuccess('');
  };

  // ── Login ──────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) return setError('Email address is required.');
    if (!password) return setError('Password is required.');

    setLoading(true);
    try {
      await login(email, password, { remember });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Sign Up ────────────────────────────────────────────────────────────────
  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!fullName.trim()) return setError('Full name is required.');
    if (!email.trim()) return setError('Email address is required.');
    if (password.length < 6) return setError('Password must be at least 6 characters.');
    if (password !== confirmPassword) return setError('Passwords do not match.');

    setLoading(true);
    try {
      const { user, session } = await signUp(email, password, fullName.trim());
      if (session) {
        // Email confirmation is disabled — session is live immediately
        navigate('/', { replace: true });
      } else if (user) {
        // Email confirmation is enabled — confirmation email sent
        setSuccess(
          'Account created! Please check your email and click the confirmation link, then sign in.'
        );
        setMode('login');
      }
    } catch (err) {
      setError(err.message || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isLogin = mode === 'login';

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4 sm:p-8">
      {/* One contained card rather than a full-bleed split: the artwork is
          inset with the form, so the page reads as a single object on the
          page instead of two halves meeting at a seam. */}
      <div className="flex w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
        {/* ── Artwork ── */}
        <div className="hidden w-1/2 p-8 lg:block">
          {/* Three bands: logo, stage, footer. Sized rather than spread with
              justify-between, so the artwork is centred on the panel instead
              of wherever the leftover space happened to put it. */}
          <div className="relative flex h-full min-h-[560px] flex-col overflow-hidden rounded-xl bg-gradient-to-br from-primary-700 via-primary-600 to-primary-500 p-8 text-white">
            {/* Ground. Kept to the corners and blurred, so it lights the
                composition without competing with the circles at its centre. */}
            <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
            <div className="pointer-events-none absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-white/[0.07] blur-2xl" />

            {/* self-start is load-bearing: this is a flex column, so the
                default align-items:stretch was overriding w-auto and pulling
                the wordmark to the full 384px column — a 2.9x stretch. */}
            <img
              src="/care-arena-logo.png"
              alt="CareArena"
              className="relative h-9 w-auto self-start object-contain brightness-0 invert"
            />

            {/* The stage. Halo, ring, chips and artwork are all children of one
                square box and all centred on it, so they share a centre by
                construction. aria-hidden: this is scenery, and nothing it names
                is actionable from the sign-in page. */}
            <div className="relative flex flex-1 items-center justify-center" aria-hidden="true">
              <div className="relative aspect-square w-full" style={{ maxWidth: STAGE }}>
                <div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{ width: HALO, height: HALO, background: HALO_FILL }}
                />

                {/* Ring and chips share one period in opposite directions, so
                    each glyph travels the circle while staying upright. */}
                <div className="orbit-ring absolute inset-0">
                  <div
                    className="absolute rounded-full border border-dashed border-white/25"
                    style={{ inset: CHIP / 2 }}
                  />

                  {CHANNELS.map(({ icon: Icon, label }, i) => {
                    const rad = (((i * 360) / CHANNELS.length - 90) * Math.PI) / 180;
                    return (
                      <div
                        key={label}
                        className="absolute left-1/2 top-1/2"
                        style={{
                          width: CHIP,
                          height: CHIP,
                          marginLeft: `calc(${Math.cos(rad).toFixed(5)} * ${ORBIT_R} - ${CHIP / 2}px)`,
                          marginTop: `calc(${Math.sin(rad).toFixed(5)} * ${ORBIT_R} - ${CHIP / 2}px)`,
                        }}
                      >
                        <div className="orbit-chip flex h-full w-full items-center justify-center rounded-full border border-white/30 bg-white/20 text-white shadow-lg shadow-primary-900/20 backdrop-blur-md">
                          <Icon size={20} strokeWidth={2} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <img
                  src={HERO_IMAGE}
                  alt=""
                  className="absolute left-1/2 top-1/2 w-auto -translate-x-1/2 -translate-y-1/2 object-contain drop-shadow-2xl"
                  style={{ height: HERO_H }}
                />
              </div>
            </div>

            <p className="relative text-xs text-white/60">
              &copy; {new Date().getFullYear()} CareArena — Powered by mAI
            </p>
          </div>
        </div>

        {/* ── Form ── */}
        <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-10 lg:w-1/2 lg:px-12">
          <div className="mx-auto w-full max-w-sm">
            <img
              src="/care-arena-logo.png"
              alt="Care Arena"
              className="mx-auto mb-8 h-9 w-auto lg:hidden"
            />

            <div className="text-center">
              <h1 className="text-3xl font-semibold tracking-[-0.02em] text-gray-900">
                {isLogin ? 'Sign in' : 'Create account'}
              </h1>
              <p className="mt-2 text-sm text-gray-500">
                {isLogin
                  ? 'Welcome back! Please sign in to continue'
                  : 'Set up your CareArena account to get started'}
              </p>
            </div>

            {error && (
              <div className="mt-6 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                <IconAuthError size={18} className="mt-0.5 flex-shrink-0 text-red-500" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            {success && (
              <div className="mt-6 flex items-start gap-2.5 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
                <IconAuthSuccess size={18} className="mt-0.5 flex-shrink-0 text-green-600" />
                <p className="text-sm text-green-700">{success}</p>
              </div>
            )}

            <form onSubmit={isLogin ? handleLogin : handleSignUp} noValidate className="mt-8 space-y-4">
              {!isLogin && (
                <Field icon={IconAuthUser}>
                  <input
                    type="text"
                    autoComplete="name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Full name"
                    aria-label="Full name"
                    className={inputCls(error && !fullName.trim())}
                    disabled={loading}
                  />
                </Field>
              )}

              <Field icon={IconEmail}>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  aria-label="Email address"
                  className={inputCls(error && !email.trim())}
                  disabled={loading}
                />
              </Field>

              <Field icon={IconPassword}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  aria-label="Password"
                  className={cn(inputCls(error && !password), 'pr-12')}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-4 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword
                    ? <IconEyeOff size={18} />
                    : <IconEye size={18} />}
                </button>
              </Field>

              {!isLogin && (
                <Field icon={IconPassword}>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    aria-label="Confirm password"
                    className={cn(inputCls(error && confirmPassword !== password), 'pr-12')}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center px-4 text-gray-400 hover:text-gray-600"
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showConfirm
                    ? <IconEyeOff size={18} />
                    : <IconEye size={18} />}
                  </button>
                </Field>
              )}

              {isLogin && (
                <label className="flex select-none items-center gap-2 pt-1 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Keep me signed in
                </label>
              )}

              <button
                type="submit"
                disabled={loading}
                className={cn(
                  'flex h-12 w-full items-center justify-center rounded-full text-sm font-semibold text-white',
                  'bg-primary-600 transition hover:bg-primary-700 active:bg-primary-800',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                  'disabled:cursor-not-allowed disabled:opacity-60'
                )}
              >
                {loading ? (
                  <>
                    <IconAuthSpinner size={18} className="mr-2 animate-spin" />
                    {isLogin ? 'Signing in…' : 'Creating account…'}
                  </>
                ) : isLogin ? (
                  'Sign in'
                ) : (
                  'Create account'
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-500">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <button
                type="button"
                onClick={() => switchMode(isLogin ? 'signup' : 'login')}
                className="font-medium text-primary-600 hover:underline"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
